import { useMemo } from 'react';
import { useCurrentGame } from '@/features/games/context/CurrentGameContext';
import { getSportModule } from './registry';
import { NHL_SPORT_MODULE } from './nhl';
import type { SportEventModule, SportEventTypeConfig } from './types';

export interface ActiveSportEventTypes {
  /** The resolved SportEventModule for the current game. Defaults to NHL when the game has no league set, preserving legacy behavior. */
  module: SportEventModule;
  /** Every event type the active sport emits. */
  eventTypes: ReadonlyArray<SportEventTypeConfig>;
  /** Event-type strings the backend custom-events endpoint should fetch. */
  backendEventTypes: string[];
  /** Event types that should render as filter pills in the panel header. */
  filterableEventTypes: SportEventTypeConfig[];
  /** Event-type strings present in the filterable pill list (convenience). */
  filterableEventTypeStrings: string[];
}

/**
 * Resolves the active sport for the current game and returns the
 * sport-scoped event-type metadata.
 *
 * Reads from `useCurrentGame()` — must be called inside a CurrentGameProvider.
 * Falls back to the NHL module when the game has no league set (matches
 * legacy behavior of treating an unknown game as hockey).
 *
 * Memoized on `game.league?.name` so re-renders don't recompute the derived
 * arrays unnecessarily.
 */
export function useActiveSportEventTypes(): ActiveSportEventTypes {
  const { game } = useCurrentGame();
  // GameModel exposes the league nested under `season`, not at the top level.
  // See apps/cloud-ui/src/features/games/types/GameModel.ts.
  const leagueName = game?.season?.league?.name;

  return useMemo<ActiveSportEventTypes>(() => {
    const module = getSportModule(leagueName) ?? NHL_SPORT_MODULE;
    const eventTypes = module.eventTypes;
    const backendEventTypes = eventTypes.filter((e) => e.fetchFromBackend).map((e) => e.type);
    const filterableEventTypes = eventTypes.filter((e) => e.showFilterPill).slice();
    const filterableEventTypeStrings = filterableEventTypes.map((e) => e.type);

    return {
      module,
      eventTypes,
      backendEventTypes,
      filterableEventTypes,
      filterableEventTypeStrings,
    };
  }, [leagueName]);
}
